import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { FacilityPicker } from "./FacilityPicker";

/**
 * Every facility we model, laid out to be chosen from rather than read about: filter by the level of the game,
 * tap the one you mean, and carry that choice straight into the planner.
 */
export const FacilityLibrary = () => {
  const [lib, setLib] = useState(null);
  const [tier, setTier] = useState("");
  const [chosen, setChosen] = useState("community_court");

  useEffect(() => {
    api.get("/facility-library").then((r) => setLib(r.data)).catch(() => setLib(null));
  }, []);

  const shown = useMemo(
    () => (lib ? lib.facilities.filter((f) => !tier || f.tier === tier) : []),
    [lib, tier],
  );

  useEffect(() => {
    if (shown.length && !shown.some((f) => f.id === chosen)) setChosen(shown[0].id);
  }, [shown, chosen]);

  if (!lib) return null;
  const facility = lib.facilities.find((f) => f.id === chosen);

  return (
    <section className="mt-16" data-testid="facility-library">
      <p className="eyebrow">Choose what gets built</p>
      <h2 className="display-2 mt-4 max-w-4xl">The {lib.facilities.length} Facilities We Model</h2>
      <p className="body-copy mt-5 max-w-3xl">
        Every facility type, labelled with the level of the game it serves, what it is for, the land it needs and
        an indicative build cost. Pick one and price it in any of the 47 counties.
      </p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="facility-library-tiers">
        {[["", "All levels"], ...lib.tiers.map((t) => [t.id, t.label])].map(([id, label]) => (
          <button
            key={id || "all"}
            type="button"
            onClick={() => setTier(id)}
            data-testid={`facility-tier-${id || "all"}`}
            className={`mono border px-3.5 py-2 text-[0.6rem] uppercase tracking-[0.14em] transition-colors ${
              tier === id ? "border-maasai bg-maasai text-white" : "border-white/15 text-zinc-300 hover:border-ochre hover:text-ochre"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="mono mt-6 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500" data-testid="facility-library-count">
        {shown.length} {shown.length === 1 ? "facility" : "facilities"} shown · tap one to select it
      </p>

      <div className="mt-4">
        <FacilityPicker facilities={shown} value={chosen} onChange={setChosen} testId="facility-library-picker" />
      </div>

      {facility ? (
        <div className="mt-6 flex flex-wrap items-center gap-4" data-testid="facility-library-selected">
          <p className="text-sm text-zinc-400">
            <span className="text-white">{facility.name}</span> selected — {facility.used_for}
          </p>
          <Link
            to={`/facility-planner?facility=${facility.id}`}
            className="btn-primary"
            data-testid="facility-library-price-it"
          >
            Price it in a county <ArrowUpRight size={14} />
          </Link>
          <Link
            to={`/compare-facilities?a=${facility.id}`}
            className="btn-ghost"
            data-testid="facility-library-compare"
          >
            Compare with another <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : null}
    </section>
  );
};
